import { useCallback, useEffect, useState } from 'react';

export interface UsePlatformAdminResult {
  /** True while the platform console is the view on screen. */
  isOpen: boolean;
  /**
   * True once the console may talk to the server about where people are: the
   * island the operator came from has been left, or there was none to leave.
   */
  isReady: boolean;
  /**
   * Open the console. An entry point that has to leave an island first hands
   * its exit over, and the console waits for it before its first request.
   */
  open: (exit?: () => Promise<void> | void) => void;
  close: () => void;
}

/**
 * Whether the superadmin console is open (§8).
 *
 * There is no router, so a view is a piece of app state like any other. It
 * lives in the app context rather than in `App.tsx` because the two entry
 * points are elsewhere: the island list header, and the in-game menu — which
 * leaves the island first.
 *
 * The flag says nothing about who may see the console; `PlatformAdminView`
 * checks the platform role itself, and the server refuses `/admin/*` to anyone
 * else whatever the client believes.
 *
 * Entering from in-game is two things happening at once — the island is left,
 * the console opens — and the order matters: presence is per-island and read
 * from the server's memory, so a snapshot taken before the socket has gone
 * shows the operator still standing where they just left. The console opens
 * immediately, so there is no flash of the island list, but it reports itself
 * *ready* only once the exit has settled, and its presence requests wait for
 * that (§6.7).
 *
 * @param isAuthenticated - whether a session is live; the console closes with it
 */
export function usePlatformAdmin(isAuthenticated: boolean): UsePlatformAdminResult {
  const [isOpen, setIsOpen] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const open = useCallback((exit?: () => Promise<void> | void) => {
    setIsOpen(true);

    if (!exit) {
      setIsReady(true);
      return;
    }

    setIsReady(false);
    // Started here and now rather than in a later microtask: the island should
    // be on its way out the moment the operator asks for the console. A failed
    // exit still opens it — a stale presence row is a better answer than a
    // screen that never arrives.
    void (async () => {
      try {
        await exit();
      } catch {
        // Nothing here can undo an exit, and the console was asked for.
      } finally {
        setIsReady(true);
      }
    })();
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setIsReady(false);
  }, []);

  // A session ending closes the console with it. Otherwise the next person to
  // sign in on this browser — or the same one after a deactivation — would
  // land straight in the platform view, which at best is not where they left
  // off and at worst is a screen they may not open at all.
  useEffect(() => {
    if (!isAuthenticated) {
      setIsOpen(false);
      setIsReady(false);
    }
  }, [isAuthenticated]);

  return { isOpen, isReady, open, close };
}
